import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Check, ChevronsUpDown, Plus, Store } from "lucide-solid";
import { cn } from "@comandero/shared";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TextField } from "@/components/ui/text-field";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";

/**
 * Selector de restaurante (organización) en el header — patrón del
 * account-switcher pero para orgs.
 *
 * Instancia SU PROPIO `useActiveContext`; comparte el restaurante activo con
 * las páginas a través del signal de módulo de `active-restaurant.ts`, así que
 * cambiar aquí re-scopea reactivamente TODO el POS (menú, comandas, caja…)
 * sin recargar ni prop-drilling.
 *
 * Se OCULTA cuando el usuario aún no tiene ningún restaurante (invitado /
 * pre-onboarding): en ese estado el `RestaurantGate` es quien conduce el alta;
 * un switcher vacío solo sería ruido.
 *
 * "Añadir restaurante" reutiliza `bootstrapRestaurant` (alta atómica
 * server-side + espera de sync + activación), con feedback de error inline
 * (familia CC-8: nunca dejar el botón pegado sin señal).
 */
export function RestaurantSwitcher() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const ctx = useActiveContext();
  const [open, setOpen] = createSignal(false);
  const [adding, setAdding] = createSignal(false);
  const [name, setName] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const canSubmit = () => Boolean(db()) && name().trim().length > 0 && !busy();

  const choose = (orgId: string) => {
    ctx.setActiveRestaurant(orgId);
    setOpen(false);
  };

  const resetAddForm = () => {
    setAdding(false);
    setName("");
    setError(null);
  };

  async function createRestaurant(e: Event) {
    e.preventDefault();
    if (!canSubmit()) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // `bootstrapRestaurant` ya deja el nuevo restaurante como ACTIVO.
      await ctx.bootstrapRestaurant(name());
      resetAddForm();
      setOpen(false);
    } catch (err) {
      console.error("[RestaurantSwitcher] add restaurant failed:", err);
      setError(t(msg`We couldn't create your restaurant. Check your connection and try again.`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Show when={ctx.orgs().length > 0}>
      <Popover
        open={open()}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            resetAddForm();
          }
        }}
      >
        <PopoverTrigger
          as="button"
          type="button"
          data-testid="restaurant-switcher"
          class={cn(
            buttonVariants({ variant: "ghost", size: "sm" }),
            "h-9 max-w-[12rem] gap-2 px-2 text-sm font-medium text-muted-foreground hover:text-foreground",
          )}
          title={t(msg`Switch restaurant`)}
          aria-label={t(msg`Switch restaurant`)}
        >
          <Store class="h-4 w-4 shrink-0" />
          <span class="truncate" data-testid="restaurant-switcher-current">
            {ctx.org()?.name ?? t(msg`Restaurant`)}
          </span>
          <ChevronsUpDown class="h-3.5 w-3.5 shrink-0 opacity-60" />
        </PopoverTrigger>
        <PopoverContent class="w-64 p-1.5">
          <p class="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Restaurants`)}
          </p>
          <div class="max-h-[40vh] space-y-0.5 overflow-y-auto">
            <For each={ctx.orgs()}>
              {(o) => {
                const isActive = () => o.id === ctx.org()?.id;
                return (
                  <button
                    type="button"
                    data-testid="restaurant-switcher-item"
                    data-active={isActive()}
                    class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/50 data-[active=true]:bg-accent/40 data-[active=true]:font-semibold"
                    onClick={() => choose(o.id)}
                  >
                    <Store class="h-4 w-4 shrink-0 opacity-70" />
                    <span class="flex-1 truncate">{o.name}</span>
                    <Show when={isActive()}>
                      <Check class="h-4 w-4 shrink-0 text-primary" />
                    </Show>
                  </button>
                );
              }}
            </For>
          </div>

          <div class="my-1 h-px bg-border/60" />

          <Show
            when={adding()}
            fallback={
              <button
                type="button"
                data-testid="restaurant-switcher-add"
                class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/50"
                onClick={() => setAdding(true)}
              >
                <Plus class="h-4 w-4 shrink-0" />
                <span>{t(msg`Add restaurant`)}</span>
              </button>
            }
          >
            <form class="space-y-2 p-1" onSubmit={createRestaurant}>
              <TextField
                data-testid="restaurant-switcher-name-input"
                placeholder={t(msg`Restaurant name`)}
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                autofocus
              />
              <div class="flex gap-2">
                <Button
                  data-testid="restaurant-switcher-create"
                  type="submit"
                  size="sm"
                  class="flex-1"
                  disabled={!canSubmit()}
                >
                  {busy() ? t(msg`Creating…`) : t(msg`Create`)}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={resetAddForm}
                  disabled={busy()}
                >
                  {t(msg`Cancel`)}
                </Button>
              </div>
              <Show when={error()}>
                {(message) => (
                  <p
                    role="alert"
                    data-testid="restaurant-switcher-error"
                    class="text-xs text-destructive"
                  >
                    {message()}
                  </p>
                )}
              </Show>
            </form>
          </Show>
        </PopoverContent>
      </Popover>
    </Show>
  );
}
